import React from 'react';

import { DOCUMENT_CHECKLIST_ITEM_STATES } from '../../constants/DocumentChecklistItemConstants';
import { LISTING_OFFER_CHANNELS } from '../../constants/ListingOfferConstants';
import { BINSR_TYPE } from '../../constants/RepairPreferenceConstants';
import {
  IAccessInfo,
  IBuyerRepairPreference,
  IDocumentChecklistItem,
  IRequestStatusMap,
  ListingOfferChannel,
  RepairPreference,
} from '../../declarations';
import { cleansePhoneNumber, formatPhoneNumberInput } from '../../helpers/formatUtils';
import Divider from '../common/Divider';
import LabeledRow from '../common/SectionCard/LabeledRow';
import SectionCard from '../common/SectionCard/SectionCard';
import SectionCardContent from '../common/SectionCard/SectionCardContent';
import CalloutBar from './CalloutBar';
import FileUploadRow from './FileUploadRow';
import RepairPreferencesModal from './RepairPreferencesModal';

import './BRNDocumentsSectionStyle.less';

export interface IBRNDocumentsSectionProps {
  accessInfo: IAccessInfo;
  additionalDocumentTypes: string[];
  buyerRepairPreference: IBuyerRepairPreference | null;
  channel: ListingOfferChannel;
  documentChecklistItems: IDocumentChecklistItem[];
  onDocumentDownload: (id: number) => void;
  onDocumentRemove: (id: number) => void;
  onDocumentSave: (
    name: string,
    documentType: string,
    fileBase64DataUrl: string,
    requestId: number,
    checklistItemId?: number,
  ) => void;
  removeChecklistDocumentRequests: IRequestStatusMap;
  repairPreferences: IBuyerRepairPreference | null;
  submitBuyerRepairPreferences: (
    notes: string | null,
    preferredSellerReponse: RepairPreference,
  ) => void;
  supportPhoneNumber: string;
  uploadChecklistDocumentRequests: IRequestStatusMap;
}

export interface IBRNDocumentsSectionState {
  showRepairPreferencesModal: boolean;
}

export default class BRNDocumentsSection extends React.PureComponent<
  IBRNDocumentsSectionProps,
  IBRNDocumentsSectionState
> {
  public state = {
    showRepairPreferencesModal: false,
  };

  public onCloseRepairPreferencesModal = () => this.setState({ showRepairPreferencesModal: false });
  public onOpenRepairPreferencesModal = () => this.setState({ showRepairPreferencesModal: true });

  public render(): JSX.Element {
    const {
      accessInfo,
      additionalDocumentTypes,
      buyerRepairPreference,
      channel,
      documentChecklistItems,
      onDocumentDownload,
      onDocumentRemove,
      onDocumentSave,
      removeChecklistDocumentRequests,
      repairPreferences,
      submitBuyerRepairPreferences,
      supportPhoneNumber,
      uploadChecklistDocumentRequests,
    } = this.props;
    const { showRepairPreferencesModal } = this.state;
    const isDirectBuyer = channel === LISTING_OFFER_CHANNELS.direct;

    return (
      <SectionCard
        className="resale-closing-dashboard-brn-documents-section"
        headerText={isDirectBuyer ? 'Repair Documents' : 'Repair Negotiation Documents'}
        id="repair-documents-section"
      >
        <SectionCardContent>
          <LabeledRow label="Home Access">
            <div>Please use the lockbox information distributed by email to enter the home.</div>
            {accessInfo.community_gate_code && (
              <LabeledRow label="Community Gate Code">{accessInfo.community_gate_code}</LabeledRow>
            )}
            {accessInfo.electrical_panel_code && (
              <LabeledRow label="Electrical Panel Code">
                {accessInfo.electrical_panel_code}
              </LabeledRow>
            )}
            {accessInfo.gate_code ? (
              <div>
                <LabeledRow label="Gate Code">{accessInfo.gate_code}</LabeledRow>
                The gate code is for the lock that we put on the side gate of any of our homes (if
                it requires 4 digits, add a 0 before that code). For assistance accessing the
                property, please call or text our support line at{' '}
                <a href={`tel:${cleansePhoneNumber(supportPhoneNumber)}`}>
                  {formatPhoneNumberInput(supportPhoneNumber)}
                </a>
                .
              </div>
            ) : (
              <div>
                For assistance accessing the property, please call or text our support line at{' '}
                <a href={`tel:${cleansePhoneNumber(supportPhoneNumber)}`}>
                  {formatPhoneNumberInput(supportPhoneNumber)}
                </a>
                .
              </div>
            )}
          </LabeledRow>
        </SectionCardContent>
        <Divider />
        <SectionCardContent>
          {documentChecklistItems
            .filter(
              item =>
                !isDirectBuyer ||
                item.state === DOCUMENT_CHECKLIST_ITEM_STATES.executed ||
                item.state === DOCUMENT_CHECKLIST_ITEM_STATES.received,
            )
            .map(item => {
              const fileUploadBoxChildren =
                item.document_type === BINSR_TYPE && repairPreferences ? (
                  <CalloutBar
                    onClick={this.onOpenRepairPreferencesModal}
                    text="View repair preferences"
                  />
                ) : null;
              return (
                <FileUploadRow
                  documentChecklistItem={item}
                  fileUploadBoxChildren={fileUploadBoxChildren}
                  key={item.id}
                  onDocumentDownload={onDocumentDownload}
                  onDocumentRemove={onDocumentRemove}
                  onDocumentSave={onDocumentSave}
                  onSaveRepairPreferences={submitBuyerRepairPreferences}
                  removeChecklistDocumentRequests={removeChecklistDocumentRequests}
                  uploadChecklistDocumentRequests={uploadChecklistDocumentRequests}
                />
              );
            })}
          {!isDirectBuyer && (
            <FileUploadRow
              additionalDocumentTypes={additionalDocumentTypes}
              onDocumentDownload={onDocumentDownload}
              onDocumentRemove={onDocumentRemove}
              onDocumentSave={onDocumentSave}
              removeChecklistDocumentRequests={removeChecklistDocumentRequests}
              uploadChecklistDocumentRequests={uploadChecklistDocumentRequests}
            />
          )}
        </SectionCardContent>
        <RepairPreferencesModal
          buyerRepairPreferences={buyerRepairPreference}
          isOpen={showRepairPreferencesModal}
          onRequestClose={this.onCloseRepairPreferencesModal}
        />
      </SectionCard>
    );
  }
}
